import React, { useRef, useState, useEffect } from 'react'
import PropTypes from 'prop-types'

import {
  View,
  Easing,
  Animated,
  Platform,
  TouchableOpacity,
  Dimensions
} from 'react-native'

// import {
//   check,
//   request,
//   RESULTS,
//   PERMISSIONS,
//   openSettings
// } from 'react-native-permissions'

import { useTheme } from '@vfgroup-oneplatform/foundation/Components/Themes'
import {
  Icon,
  VFText,
  VFButton,
  QuickActionMenu
} from '@vfgroup-oneplatform/foundation/Components'
import ContactList from '@vfgroup-oneplatform/framework/CommonUI/ContactList/ContactList'

import MigrationLottie from '../MigrationLottie'
import VFTextWithLoader from '../VFTextWithLoader'
import MembersLoading from '../../LoadingScreens/MembersLoading'

import CustomInput from '../CustomInput/CustomInput'

import { STATUS } from '../../Utils/members'

import styles from './MembersList.Styles'

import { UhImages, getThemeImages } from '../../Themes'
import ReactNativeModal from 'react-native-modal'

const validate = ({ value, fn, text }) => {
  const formatError = fn(value) ? '' : text
  const message = formatError
  const isError = Boolean(formatError)
  return { message, isError }
}

const getSlotCount = val => {
  const count = val ? val.split(' ')[0] : ''
  return count ? parseInt(count, 10) : 0
}

const defaultState = {
  number: '',
  show: false
}

const getCount = members => {
  if (!Array.isArray(members)) {
    return 0
  }
  return members.filter(member => member.role !== 'admin')?.length ?? 0
}

const MembersList = ({
  onAdd,
  members,
  onRetry,
  onRemove,
  viewOnly,
  isLoading,
  groupCount,
  isGroupAdmin,
  loggedInNumber,
  showHeaderCount,
  hideInputOnFill,
  numberFormatter,
  numberValidator
}) => {
  const [phoneNumber, setPhoneNumber] = useState('')
  const [contentWidth, setContentWidth] = useState(225)
  const [addWarning, setAddWarning] = useState(defaultState)
  const [removeWarning, setRemoveWarning] = useState(defaultState)

  const maxMembers = getSlotCount(groupCount)

  const theme = useTheme()

  const handleMemberRemove = data => {
    // If the user has failed to be added do not show warning
    if (data.member.status === STATUS.error) {
      onRemove(data.member.number)
    } else {
      setRemoveWarning({
        show: true,
        id: data.member.id,
        isLeaving: data.isLeaving,
        number: data.member.number
      })
    }
  }

  const handleAddClick = data => {
    setAddWarning({
      show: true,
      number: data.number
    })
  }

  const removeMember = () => {
    setRemoveWarning(prev => {
      return { ...prev, show: false }
    })
    onRemove(removeWarning.number)
  }

  const addMember = () => {
    setAddWarning(prev => {
      return { ...prev, show: false }
    })
    onAdd(addWarning.number)
  }

  const handleLayout = event => {
    if (event.nativeEvent && event.nativeEvent.layout) {
      const { width } = event.nativeEvent.layout
      setContentWidth((width ?? 0) / 2)
    }
  }

  return (
    <View style={styles.card(theme)} onLayout={handleLayout}>
      <CardHeader
        isLoading={isLoading}
        groupCount={maxMembers.toString()}
        title="unlimited_my_members_short"
        memberCount={getCount(members).toString()}
        showHeaderCount={showHeaderCount && isGroupAdmin}
      />
      {isLoading ? (
        <MembersLoading count={3} spacing={5} width={contentWidth} />
      ) : (
        <Members
          members={members}
          onRetry={onRetry}
          viewOnly={viewOnly}
          isGroupAdmin={isGroupAdmin}
          onRemove={handleMemberRemove}
          loggedInNumber={loggedInNumber}
        />
      )}
      {!isLoading && isGroupAdmin ? (
        <AddMemberForm
          showHint
          showAddButton
          members={members}
          viewOnly={viewOnly}
          number={phoneNumber}
          onAdd={handleAddClick}
          groupCount={maxMembers}
          onChange={setPhoneNumber}
          formatFn={numberFormatter}
          validateFn={numberValidator}
          loggedInNumber={loggedInNumber}
          hideInputOnFill={hideInputOnFill}
        />
      ) : null}
      <WarningModal
        onConfirm={removeMember}
        image={UhImages.leave}
        show={removeWarning.show}
        number={removeWarning.number}
        title={
          removeWarning.isLeaving
            ? 'unlimited_leave_title'
            : 'unlimited_kick_member_title'
        }
        header={
          removeWarning.isLeaving
            ? 'unlimited_leave_heading'
            : 'unlimited_kick_member_heading'
        }
        onClose={() => setRemoveWarning(defaultState)}
        description={
          removeWarning.isLeaving ? '' : 'unlimited_kick_member_sub_heading'
        }
      />
      <WarningModal
        onConfirm={addMember}
        show={addWarning.show}
        number={addWarning.number}
        image={UhImages.add_member}
        title="unlimited_add_member_title"
        header="unlimited_add_member_heading"
        onClose={() => setAddWarning(defaultState)}
        description="unlimited_kick_member_sub_heading"
      />
    </View>
  )
}

const CardHeader = ({
  title,
  isLoading,
  groupCount,
  memberCount,
  showHeaderCount
}) => {
  const theme = useTheme()
  const themedImages = getThemeImages(theme.name)
  return (
    <View style={styles.header}>
      <Icon
        size={32}
        type="image"
        style={styles.icon}
        name={themedImages.members}
      />
      <View style={styles.headerText}>
        <VFText i18nKey="unlimited_my_members" style={styles.subtitle} />
        <View style={styles.headerContent}>
          <VFText i18nKey={title} style={styles.title} />
          {showHeaderCount ? (
            <VFTextWithLoader
              loaderWidth={50}
              loaderHeight={14}
              isLoading={isLoading}
              style={styles.memberCount}
              i18nKey="unlimited_members_count"
              placeHolders={[memberCount, groupCount]}
            />
          ) : null}
        </View>
      </View>
    </View>
  )
}

const Members = ({
  members,
  onRetry,
  onRemove,
  viewOnly,
  isGroupAdmin,
  loggedInNumber
}) => {
  if (!members.length) {
    return (
      <MemberPlaceholder
        viewOnly={viewOnly}
        text={
          viewOnly
            ? 'unlimited_no_members_placeholder'
            : 'unlimited_members_placeholder'
        }
      />
    )
  }
  return (
    <View>
      {members.map((member, index) => (
        <Member
          key={index}
          member={member}
          viewOnly={viewOnly}
          isGroupAdmin={isGroupAdmin}
          loggedInNumber={loggedInNumber}
          onRetry={() => onRetry({ index, member })}
          onRemove={isLeaving => onRemove({ index, member, isLeaving })}
        />
      ))}
    </View>
  )
}

const Member = ({
  member,
  onRetry,
  onRemove,
  viewOnly,
  isGroupAdmin,
  loggedInNumber
}) => {
  return (
    <View style={styles.member}>
      <View>
        <VFText
          i18nKey="%1$s"
          placeHolders={[member.number]}
          style={[styles.text, member.isError ? styles.error : '']}
        />
        {!viewOnly ? (
          <View style={styles.status}>
            <MemberStatus
              status={member.status}
              isGroupAdmin={isGroupAdmin}
              acceptedDate={member.acceptedDate}
              isGracePeriod={member.isGracePeriod}
              gracePeriodEndDate={member.gracePeriodEndDate}
            />
          </View>
        ) : null}
      </View>
      {!viewOnly ? (
        <MemberAction
          onRetry={onRetry}
          role={member.role}
          onRemove={onRemove}
          error={member.error}
          number={member.number}
          status={member.status}
          isGroupAdmin={isGroupAdmin}
          loggedInNumber={loggedInNumber}
          isGracePeriod={member.isGracePeriod}
        />
      ) : null}
    </View>
  )
}

const MemberStatus = ({
  status,
  acceptedDate,
  isGroupAdmin,
  isGracePeriod,
  gracePeriodEndDate
}) => {
  if (status === STATUS.temporary) {
    return (
      <VFText i18nKey="unlimited_member_success_added" style={styles.success} />
    )
  } else if (status === STATUS.accepted) {
    if (isGracePeriod) {
      return (
        <VFText
          i18nKey={
            isGroupAdmin
              ? 'unlimited_grace_period_status'
              : 'unlimited_leave_group_explain'
          }
          placeHolders={[gracePeriodEndDate]}
        />
      )
    } else {
      return <VFText i18nKey="%1$s" placeHolders={[acceptedDate]} />
    }
  } else if (status === STATUS.waiting_confirmation) {
    return <VFText i18nKey="unlimited_pending_status" style={styles.orange} />
  } else if (status === STATUS.marked_for_leave) {
    return <VFText i18nKey="unlimited_marked_for_leave" style={styles.red} />
  }

  return null
}

const MemberAction = ({
  role,
  error,
  status,
  number,
  onRetry,
  onRemove,
  isGroupAdmin,
  isGracePeriod,
  loggedInNumber
}) => {
  const [showError, setShowError] = useState(false)
  const bgSize = useRef(new Animated.Value(0)).current

  const bgSizeInterpolated = bgSize.interpolate({
    inputRange: [0, 1],
    outputRange: [0, 1]
  })

  const isMyNumber = loggedInNumber === number

  const onShowErrorClick = () => setShowError(true)

  const handleErrorRetry = () => {
    setShowError(false)
    onRetry()
  }

  const handleErrorRemove = () => {
    setShowError(false)
    onRemove(isMyNumber)
  }

  useEffect(() => {
    if (status === STATUS.error) {
      Animated.loop(
        Animated.timing(bgSize, {
          duration: 1000,
          easing: Easing.linear,
          useNativeDriver: true,
          toValue: 1
        })
      ).start()
    } else {
      bgSize.stopAnimation()
    }

    return () => {
      bgSize.stopAnimation()
    }
  }, [bgSize, status])

  if (status === STATUS.loading) {
    return (
      <View style={styles.lottieHolder}>
        <MigrationLottie style={styles.lottie} />
      </View>
    )
  } else if (role === 'admin') {
    return <VFText i18nKey="unlimited_admin_member" />
  } else if (status === STATUS.error) {
    return (
      <>
        <TouchableOpacity
          activeOpacity={0.4}
          onPress={onShowErrorClick}
          style={styles.relativeParent}
        >
          <>
            <Animated.View
              style={[
                styles.pulsingBg,
                {
                  transform: [
                    {
                      scale: bgSizeInterpolated
                    }
                  ]
                }
              ]}
            />
            <Icon
              size={20}
              type="image"
              name={UhImages.error}
              style={styles.absoluteChild}
            />
          </>
        </TouchableOpacity>
        <NumberErrorDialog
          error={error}
          number={number}
          show={showError}
          onRetry={handleErrorRetry}
          onRemove={handleErrorRemove}
          onClose={() => setShowError(false)}
        />
      </>
    )
  } else if (
    status === STATUS.temporary ||
    (status === STATUS.accepted && !isGracePeriod)
  ) {
    return isGroupAdmin ? (
      <TouchableOpacity
        onPress={() => onRemove(isMyNumber)}
        activeOpacity={0.4}
        style={styles.memberStatus}
      >
        <Icon
          size={20}
          type="image"
          style={styles.memberIcon}
          name={UhImages.delete}
        />
      </TouchableOpacity>
    ) : isMyNumber ? (
      <TouchableOpacity
        activeOpacity={0.4}
        onPress={() => onRemove(isMyNumber)}
        style={styles.memberStatus}
      >
        <VFText
          i18nKey="unlimited_leave_group_button"
          style={styles.leaveGroup}
        />
        <Icon
          size={20}
          type="image"
          style={styles.memberIcon}
          name={UhImages.leave_icon}
        />
      </TouchableOpacity>
    ) : null
  }
  return null
}

const errorDefaults = {
  isError: false,
  message: ''
}

const AddMemberForm = ({
  onAdd,
  number,
  members,
  onChange,
  showHint,
  formatFn,
  viewOnly,
  groupCount,
  validateFn,
  showAddButton,
  loggedInNumber,
  hideInputOnFill
}) => {
  const [inputRef, setInputRef] = useState(null)
  const [error, setError] = useState(errorDefaults)
  const [showContacts, setShowContacts] = useState(false)

  const theme = useTheme()
  const themedImages = getThemeImages(theme.name)

  const isInputDisabled = groupCount <= getCount(members)

  const addNewMember = () => {
    const data = handleValidation(number)
    if (data.isError) {
      return
    }
    onAdd({ number: formatFn(number) })
    // Resetting input field
    inputRef && inputRef.setValue('')
    onChange('')
  }

  const handleContactPress = selected => {
    if (!inputRef) {
      return
    }
    const formatted = (selected ?? '').replace(/\s/g, '')
    handleValidation(formatted)
    // Updating input field
    onChange(selected)
    inputRef.setValue(selected)
    setShowContacts(false)
  }

  const handleValidation = value => {
    const data = validate({
      value: value,
      fn: validateFn,
      text: 'unlimited_invalid_number'
    })
    setError(data)
    if (!data.isError) {
      const secondValidation = checkIfAdminOrAdded(
        value,
        loggedInNumber,
        members
      )
      setError(secondValidation)
      return secondValidation
    }
    return data
  }

  const checkIfAdminOrAdded = (num, admin, membersArr) => {
    const formatted = formatFn(num)
    if (formatted === admin) {
      return { message: 'unlimited_admin_number', isError: true }
    }
    for (const member of membersArr) {
      if (member.number === formatted) {
        return { message: 'unlimited_already_added', isError: true }
      }
    }
    return { message: '', isError: false }
  }

  // const CONTACTS_PERMISSION =
  //   Platform.OS === 'ios'
  //     ? PERMISSIONS.IOS.CONTACTS
  //     : PERMISSIONS.ANDROID.READ_CONTACTS

  const openContacts = () => {
    // if (showContacts) {
    //   setShowContacts(false)
    //   return
    // }
    // check(CONTACTS_PERMISSION).then(status => {
    //   switch (status) {
    //     case RESULTS.DENIED:
    //       request(CONTACTS_PERMISSION).then(result => {
    //         if (result === RESULTS.GRANTED) {
    //           setShowContacts(true)
    //         }
    //         if (
    //           (result === RESULTS.BLOCKED || result === RESULTS.DENIED) &&
    //           Platform.OS !== 'ios'
    //         ) {
    //           openSettings()
    //         }
    //       })
    //       break
    //     case RESULTS.GRANTED:
    //       setShowContacts(true)
    //       break
    //     case RESULTS.BLOCKED:
    //       openSettings()
    //       break
    //     default:
    //       break
    //   }
    // })
  }

  if ((isInputDisabled && hideInputOnFill) || viewOnly) {
    return null
  }

  return (
    <View>
      <CustomInput
        showRightIcon
        value={number}
        fieldRef={setInputRef}
        onChangeText={onChange}
        isError={error.isError}
        keyboardType="phone-pad"
        disabled={isInputDisabled}
        validator={handleValidation}
        errorMessage={error.message}
        // rightIconPress={openContacts}
        label="unlimited_add_member_number_input"
        // rightIconName={themedImages.icCallsContacts}
        resetValidation={() => setError(errorDefaults)}
      />
      {showAddButton ? (
        <View style={styles.helperText}>
          <VFButton
            onPress={addNewMember}
            disabled={isInputDisabled}
            title="unlimited_add_member_input"
          />
        </View>
      ) : null}
      {showHint ? (
        <VFText
          style={styles.helperText}
          placeHolders={[groupCount]}
          i18nKey="unlimited_add_member_info"
        />
      ) : null}
      {/* <QuickActionMenu
        type="secondary"
        isVisible={showContacts}
        toggleModal={openContacts}
        title="top_up_someone_else_contact_list_section_title"
      >
        <ContactList onPressContact={handleContactPress} />
      </QuickActionMenu> */}
    </View>
  )
}

const WarningModal = ({
  show,
  title,
  image,
  header,
  number,
  onClose,
  onConfirm,
  description
}) => {
  const theme = useTheme()
  if (Platform.OS == 'web' && Dimensions.get('window').width > 700) {
    return (
      <ReactNativeModal
        isVisible={show}
        backdropOpacity={0.5}
        onSwipeComplete={onClose}
        onBackdropPress={onClose}
        propagateSwipe
        hideModalContentWhileAnimating
        hardwareAccelerated
        backdropTransitionOutTiming={0}
        style={{
          justifyContent: 'flex-end',
          margin: '0 !important',
          minWidth: 1920,
          maxWidth: 1920,
        }}
      >
        <View
          style={{
            minWidth: '600px',
            maxWidth: '600px',
            height: 'auto',
            backgroundColor: theme.colors.quickActionGradientColors.secondary,
            margin: 'auto',
            padding: '20px',
            paddingTop: null,
            borderRadius: "7px",
          }}
          onStartShouldSetResponder={() => true}>
          <View style={styles.kickContainer}>
            <View style={styles.kickMainContent}>
              <Icon name={image} size={120} type="image" />
              <VFText
                i18nKey={header}
                placeHolders={[number]}
                style={styles.kickTitle}
              />
              <VFText
                i18nKey={description}
                placeHolders={[number]}
                style={styles.kickDescription}
              />
            </View>
            <VFButton
              onPress={onConfirm}
              title="unlimited_continue"
              style={styles.kickButton}
            />
            <VFButton
              onPress={onClose}
              type="secondary"
              title="unlimited_back"
              style={styles.backButton}
            />
          </View>
        </View>
      </ReactNativeModal>

    )
  }
  return (
    <QuickActionMenu
      title={title}
      type="secondary"
      isVisible={show}
      toggleModal={onClose}
    >
      <View style={styles.kickContainer}>
        <View style={styles.kickMainContent}>
          <Icon name={image} size={120} type="image" />
          <VFText
            i18nKey={header}
            placeHolders={[number]}
            style={styles.kickTitle}
          />
          <VFText
            i18nKey={description}
            placeHolders={[number]}
            style={styles.kickDescription}
          />
        </View>
        <VFButton
          onPress={onConfirm}
          title="unlimited_continue"
          style={styles.kickButton}
        />
        <VFButton
          onPress={onClose}
          type="secondary"
          title="unlimited_back"
          style={styles.backButton}
        />
      </View>
    </QuickActionMenu>
  )
}

const MemberPlaceholder = ({ viewOnly, text }) => {
  const theme = useTheme()
  return (
    <View style={styles.noMembers(theme)}>
      {!viewOnly ? (
        <View style={styles.placeholderIcon}>
          <Icon name={UhImages.member_placeholder} size={73} />
        </View>
      ) : null}
      <VFText i18nKey={text} style={styles.noMemberText(theme)} />
    </View>
  )
}

const NumberErrorDialog = ({
  show,
  error,
  number,
  onClose,
  onRetry,
  onRemove
}) => {
  const theme = useTheme()
  if (Platform.OS == 'web' && Dimensions.get('window').width > 700) {
    return (
      <ReactNativeModal
        isVisible={show}
        backdropOpacity={0.5}
        onSwipeComplete={onClose}
        onBackdropPress={onClose}
        propagateSwipe
        hideModalContentWhileAnimating
        hardwareAccelerated
        backdropTransitionOutTiming={0}
        style={{
          justifyContent: 'flex-end',
          margin: '0 !important',
          minWidth: 1920,
          maxWidth: 1920,
        }}
      >
        <View
          style={{
            minWidth: '600px',
            maxWidth: '600px',
            height: 'auto',
            backgroundColor: theme.colors.quickActionGradientColors.secondary,
            margin: 'auto',
            padding: '20px',
            paddingTop: null,
            borderRadius: "7px",
          }}
          onStartShouldSetResponder={() => true}>
          <View style={[styles.kickContainer, styles.pb(4)]}>
            <View style={styles.kickMainContent}>
              <Icon name={UhImages.leave} size={120} type="image" />
              <VFText
                placeHolders={[number]}
                style={styles.kickTitle}
                i18nKey="unlimited_unable_to_add"
              />
              <VFText
                i18nKey="%1$s"
                placeHolders={[error]}
                style={styles.kickDescription}
              />
            </View>
            <VFButton
              onPress={onRetry}
              style={styles.kickButton}
              title="unlimited_try_again"
            />
            <VFButton
              type="secondary"
              onPress={onRemove}
              style={styles.backButton}
              title="unlimited_continue_without_member"
            />
          </View>
        </View>
      </ReactNativeModal>

    )
  }
  return (
    <QuickActionMenu
      isVisible={show}
      type="secondary"
      toggleModal={onClose}
      title="unlimited_error_adding_title"
    >
      <View style={[styles.kickContainer, styles.pb(4)]}>
        <View style={styles.kickMainContent}>
          <Icon name={UhImages.leave} size={120} type="image" />
          <VFText
            placeHolders={[number]}
            style={styles.kickTitle}
            i18nKey="unlimited_unable_to_add"
          />
          <VFText
            i18nKey="%1$s"
            placeHolders={[error]}
            style={styles.kickDescription}
          />
        </View>
        <VFButton
          onPress={onRetry}
          style={styles.kickButton}
          title="unlimited_try_again"
        />
        <VFButton
          type="secondary"
          onPress={onRemove}
          style={styles.backButton}
          title="unlimited_continue_without_member"
        />
      </View>
    </QuickActionMenu>
  )
}
const ErrorType = PropTypes.objectOf({
  code: PropTypes.string,
  title: PropTypes.string,
  description: PropTypes.string
})

const MembersType = PropTypes.arrayOf(
  PropTypes.objectOf({
    error: ErrorType,
    isError: PropTypes.bool,
    isAdmin: PropTypes.bool,
    number: PropTypes.string,
    isLoading: PropTypes.bool,
    isPending: PropTypes.bool,
    isInGracePeriod: PropTypes.bool
  })
)



MembersList.propTypes = {
  members: MembersType,
  onAdd: PropTypes.func,
  onRetry: PropTypes.func,
  viewOnly: PropTypes.bool,
  onRemove: PropTypes.func,
  isLoading: PropTypes.bool,
  groupCount: PropTypes.number,
  isGroupAdmin: PropTypes.bool,
  showHeaderCount: PropTypes.bool,
  hideInputOnFill: PropTypes.bool,
  loggedInNumber: PropTypes.string,
  numberFormatter: PropTypes.func,
  numberValidator: PropTypes.func
}

MembersList.defaultProps = {
  members: [],
  groupCount: 0,
  viewOnly: false,
  onAdd: () => { },
  isLoading: false,
  onRetry: () => { },
  onRemove: () => { },
  isGroupAdmin: true,
  loggedInNumber: '',
  showHeaderCount: false,
  hideInputOnFill: false
}

CardHeader.propTypes = {
  title: PropTypes.string,
  isLoading: PropTypes.bool,
  groupCount: PropTypes.number,
  memberCount: PropTypes.number,
  showHeaderCount: PropTypes.bool
}

Members.propTypes = {
  members: MembersType,
  onRetry: PropTypes.func,
  onRemove: PropTypes.func,
  viewOnly: PropTypes.bool,
  isGroupAdmin: PropTypes.bool,
  loggedInNumber: PropTypes.string
}

Member.propTypes = {
  onRetry: PropTypes.func,
  member: PropTypes.string,
  viewOnly: PropTypes.bool,
  onRemove: PropTypes.func,
  isGroupAdmin: PropTypes.bool,
  loggedInNumber: PropTypes.string
}

MemberStatus.propTypes = {
  status: PropTypes.string,
  isGroupAdmin: PropTypes.bool,
  isGracePeriod: PropTypes.bool,
  acceptedDate: PropTypes.string,
  gracePeriodEndDate: PropTypes.string
}

MemberAction.propTypes = {
  error: ErrorType,
  role: PropTypes.string,
  status: PropTypes.string,
  number: PropTypes.string,
  onRetry: PropTypes.func,
  onRemove: PropTypes.func,
  isGroupAdmin: PropTypes.bool,
  isGracePeriod: PropTypes.bool,
  loggedInNumber: PropTypes.string
}

AddMemberForm.propTypes = {
  members: MembersType,
  onAdd: PropTypes.func,
  number: PropTypes.string,
  onChange: PropTypes.func,
  showHint: PropTypes.bool,
  viewOnly: PropTypes.bool,
  formatFn: PropTypes.func,
  validateFn: PropTypes.func,
  groupCount: PropTypes.number,
  showAddButton: PropTypes.bool,
  hideInputOnFill: PropTypes.bool,
  loggedInNumber: PropTypes.string
}

WarningModal.propTypes = {
  show: PropTypes.bool,
  onClose: PropTypes.func,
  image: PropTypes.string,
  title: PropTypes.string,
  number: PropTypes.string,
  header: PropTypes.string,
  onConfirm: PropTypes.func,
  description: PropTypes.string
}

NumberErrorDialog.propTypes = {
  error: ErrorType,
  show: PropTypes.bool,
  onRetry: PropTypes.func,
  onClose: PropTypes.func,
  onRemove: PropTypes.func,
  number: PropTypes.string
}

MemberPlaceholder.propTypes = {
  viewOnly: PropTypes.bool,
  text: PropTypes.string
}

MemberPlaceholder.defaultProps = {
  viewOnly: false,
  text: 'unlimited_members_placeholder'
}

export default MembersList
export { CardHeader, AddMemberForm, Members, MemberPlaceholder }
